/**
 * Money in against money out.
 *
 * Income is money COLLECTED, not money invoiced. Setting an invoice against an
 * expense would compare a promise with a payment, and a desk whose clients pay
 * late would read as profitable in exactly the month it ran out of cash. The
 * footnote says so on the page, because this is the one figure somebody will
 * quote in a meeting.
 */
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { fmtMoney, fmtPct } from "../../ui";
import { Breakdown, MonthChart, Stat, StatStrip, monthLabel } from "./bits";
import type { ProfitReport } from "../../../lib/reports";

export function ProfitReportTab({ data }: { data: ProfitReport }) {
  const t = useT();
  const { locale } = useI18n();
  const { totals } = data;

  return (
    <>
      <StatStrip columns={4}>
        <Stat label={t("reports.profit.income")} value={fmtMoney(totals.incomeCents)} tone="ok" />
        <Stat label={t("reports.profit.expense")} value={fmtMoney(totals.expenseCents)} sub={t("reports.profit.plusTax", { v: fmtMoney(totals.expenseTaxCents) })} />
        <Stat
          label={t("reports.profit.net")}
          value={fmtMoney(totals.netCents)}
          tone={totals.netCents >= 0 ? "ok" : "bad"}
        />
        <Stat
          label={t("reports.profit.margin")}
          // Null rather than zero on no income: a month that spent money and took
          // none in has no margin, and "0%" reads as break-even.
          value={totals.marginPct === null ? "-" : fmtPct(totals.marginPct)}
          tone={totals.marginPct === null ? undefined : totals.marginPct >= 0 ? "ok" : "bad"}
        />
      </StatStrip>

      <div className="crm-rep-grid">
        <MonthChart
          title={t("reports.profit.chartTitle")}
          sub={t("reports.profit.chartSub")}
          months={data.meta.months}
          locale={locale}
          lines={[
            { label: t("reports.profit.income"), values: data.months.map((m) => m.incomeCents / 100), color: "var(--color-success)" },
            { label: t("reports.profit.expense"), values: data.months.map((m) => m.expenseCents / 100), color: "var(--color-warning)" },
          ]}
        />
      </div>

      <div className="crm-rep-grid">
        <Breakdown
          title={t("reports.profit.byMonth")}
          sub={t("reports.profit.byMonthSub")}
          // Ranked by the SIZE of the result rather than by its sign, so the
          // worst month is as visible as the best one - a loss drawn as a short
          // bar would be the month you most need to see.
          rows={data.months.map((m) => ({
            key: m.month,
            label: monthLabel(m.month, locale),
            weight: m.netCents,
            value: fmtMoney(m.netCents),
            sub: `${fmtMoney(m.incomeCents)} / ${fmtMoney(m.expenseCents)}`,
            color: m.netCents >= 0 ? "var(--color-success)" : "var(--color-destructive)",
          }))}
          empty={t("reports.empty")}
        />
      </div>

      <p className="crm-rep-note">{t("reports.profit.note")}</p>
    </>
  );
}
